import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/Icon';
import PageHeader from '../components/PageHeader';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { ApiError } from '../lib/api';
import {
  createAutomationRequest,
  deleteAutomationRequest,
  disableAutomationRequest,
  enableAutomationRequest,
  getAutomationRequest,
  listAutomationsRequest,
  updateAutomationRequest,
  type AutomationActionType,
  type AutomationMatchType,
  type AutomationTriggerType,
  type CreateAutomationPayload,
  type ManagedAutomation,
} from '../lib/automations-api';
import { listInstagramAccountsRequest, type InstagramAccount } from '../lib/instagram-api';

type ListStatus = 'loading' | 'ready' | 'error';
type AccountsStatus = 'loading' | 'ready' | 'error';
type RowErrorKind = 'toggle' | 'edit';

interface RowError {
  kind: RowErrorKind;
  message: string;
}

interface FormAction {
  action_type: '' | AutomationActionType;
  text: string;
}

interface FormState {
  name: string;
  accountId: string;
  triggerType: '' | AutomationTriggerType;
  matchType: '' | AutomationMatchType;
  keywords: string[];
  keywordDraft: string;
  mediaId: string;
  actions: FormAction[];
  priority: string;
}

interface FormErrors {
  name?: string;
  accountId?: string;
  triggerType?: string;
  matchType?: string;
  keywords?: string;
  mediaId?: string;
  priority?: string;
  actions?: Array<string | undefined>;
}

const LIST_LIMIT = 100;
const MAX_ACTIONS = 4;
const MEDIA_ID_PATTERN = /^[0-9]{1,64}$/;

const TRIGGER_OPTIONS: Array<{ value: AutomationTriggerType; label: string }> = [
  { value: 'comment', label: 'کامنت' },
  { value: 'dm', label: 'پیام خصوصی (دایرکت)' },
  { value: 'story_reply', label: 'پاسخ به استوری' },
];

const MATCH_OPTIONS: Array<{ value: AutomationMatchType; label: string }> = [
  { value: 'exact', label: 'برابر دقیق' },
  { value: 'contains', label: 'شامل' },
  { value: 'starts_with', label: 'شروع با' },
  { value: 'ends_with', label: 'پایان با' },
];

const ACTION_LABELS: Record<AutomationActionType, string> = {
  reply_comment: 'پاسخ کامنت',
  private_reply: 'پاسخ خصوصی',
  send_dm_text: 'پیام متنی',
};

/**
 * The Phase 17 trigger → action compatibility matrix: only executable
 * pairings are offered, mirroring the backend `ACTION_TYPES_BY_TRIGGER`.
 */
const ACTION_OPTIONS_BY_TRIGGER: Record<AutomationTriggerType, readonly AutomationActionType[]> = {
  comment: ['reply_comment', 'private_reply'],
  dm: ['send_dm_text'],
  story_reply: ['send_dm_text'],
};

const TRIGGER_LABELS: Record<AutomationTriggerType, string> = {
  comment: 'کامنت',
  dm: 'پیام خصوصی',
  story_reply: 'پاسخ استوری',
};

const MATCH_LABELS: Record<AutomationMatchType, string> = {
  exact: 'برابر دقیق',
  contains: 'شامل',
  starts_with: 'شروع با',
  ends_with: 'پایان با',
};

function emptyForm(): FormState {
  return {
    name: '',
    accountId: '',
    triggerType: '',
    matchType: '',
    keywords: [],
    keywordDraft: '',
    mediaId: '',
    actions: [{ action_type: '', text: '' }],
    priority: '100',
  };
}

function formFromAutomation(automation: ManagedAutomation): FormState {
  return {
    name: automation.name,
    accountId: automation.instagram_account_id,
    triggerType: automation.trigger.type,
    matchType: automation.trigger.match_type,
    keywords: [...automation.trigger.keywords],
    keywordDraft: '',
    mediaId: automation.trigger.media_id ?? '',
    actions: automation.actions.map((action) => ({
      action_type: action.action_type,
      text: action.text,
    })),
    priority: String(automation.priority),
  };
}

/**
 * Same deterministic order the backend returns (priority ASC → created_at
 * ASC → id ASC) so locally merged rows keep the server's ordering.
 */
function sortAutomations(items: ManagedAutomation[]): ManagedAutomation[] {
  return [...items].sort((a, b) => {
    if (a.priority !== b.priority) {
      return a.priority - b.priority;
    }
    if (a.created_at !== b.created_at) {
      return a.created_at - b.created_at;
    }
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}

function mergeAutomation(
  current: ManagedAutomation[],
  automation: ManagedAutomation,
): ManagedAutomation[] {
  const exists = current.some((row) => row.id === automation.id);
  const next = exists
    ? current.map((row) => (row.id === automation.id ? automation : row))
    : [...current, automation];
  return sortAutomations(next);
}

function validateForm(form: FormState): FormErrors {
  const errors: FormErrors = {};
  const name = form.name.trim();
  if (name === '') {
    errors.name = 'نام اتوماسیون الزامی است.';
  } else if (name.length > 100) {
    errors.name = 'نام اتوماسیون حداکثر ۱۰۰ نویسه است.';
  }
  if (form.accountId === '') {
    errors.accountId = 'حساب اینستاگرام را انتخاب کنید.';
  }
  if (form.triggerType === '') {
    errors.triggerType = 'رویداد مبدا را انتخاب کنید.';
  }
  if (form.matchType === '') {
    errors.matchType = 'عملگر تطبیق را انتخاب کنید.';
  }
  if (form.keywords.length === 0) {
    errors.keywords = 'حداقل یک کلیدواژه اضافه کنید.';
  }
  const mediaId = form.mediaId.trim();
  if (form.triggerType === 'comment' && mediaId !== '' && !MEDIA_ID_PATTERN.test(mediaId)) {
    errors.mediaId = 'شناسه رسانه باید فقط شامل ارقام باشد.';
  }
  const priorityText = form.priority.trim();
  const priority = Number(priorityText);
  if (priorityText === '' || !Number.isInteger(priority) || priority < 0 || priority > 1000) {
    errors.priority = 'اولویت باید عدد صحیحی بین ۰ تا ۱۰۰۰ باشد.';
  }
  const allowedActions = form.triggerType === '' ? [] : ACTION_OPTIONS_BY_TRIGGER[form.triggerType];
  const actionErrors = form.actions.map((action) => {
    if (action.action_type === '') {
      return 'نوع اکشن را انتخاب کنید.';
    }
    if (!allowedActions.includes(action.action_type)) {
      return 'این اکشن با رویداد انتخاب‌شده سازگار نیست.';
    }
    if (action.text.trim() === '') {
      return 'متن اکشن الزامی است.';
    }
    if (action.text.trim().length > 2000) {
      return 'متن اکشن حداکثر ۲۰۰۰ نویسه است.';
    }
    return undefined;
  });
  if (actionErrors.some((message) => message !== undefined)) {
    errors.actions = actionErrors;
  }
  return errors;
}

function hasFormErrors(errors: FormErrors): boolean {
  return (
    errors.name !== undefined ||
    errors.accountId !== undefined ||
    errors.triggerType !== undefined ||
    errors.matchType !== undefined ||
    errors.keywords !== undefined ||
    errors.mediaId !== undefined ||
    errors.priority !== undefined ||
    errors.actions !== undefined
  );
}

function buildPayload(form: FormState): CreateAutomationPayload {
  const mediaId = form.mediaId.trim();
  return {
    instagram_account_id: form.accountId,
    name: form.name.trim(),
    priority: Number(form.priority.trim()),
    trigger: {
      type: form.triggerType as AutomationTriggerType,
      match_type: form.matchType as AutomationMatchType,
      keywords: form.keywords,
      ...(form.triggerType === 'comment' && mediaId !== '' ? { media_id: mediaId } : {}),
    },
    actions: form.actions.map((action) => ({
      action_type: action.action_type as AutomationActionType,
      text: action.text.trim(),
    })),
  };
}

/**
 * Generic Persian error mapping — raw server messages (which may contain
 * internal details such as validation paths) are never shown to the user.
 */
function mutationErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return 'برای انجام این عملیات دوباره وارد حساب خود شوید.';
    }
    if (error.status === 404) {
      return 'این اتوماسیون یافت نشد یا به حساب دیگری تعلق دارد.';
    }
    if (error.status === 400) {
      return 'اطلاعات واردشده معتبر نیست. فرم را بررسی و دوباره تلاش کنید.';
    }
    if (error.status >= 500) {
      return 'خطای سرور رخ داد. کمی بعد دوباره تلاش کنید.';
    }
    return 'انجام عملیات ناموفق بود. دوباره تلاش کنید.';
  }
  if (error instanceof TypeError) {
    return 'ارتباط با سرور برقرار نشد. دوباره تلاش کنید.';
  }
  return 'انجام عملیات ناموفق بود. دوباره تلاش کنید.';
}

function listErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.status >= 500) {
    return 'خطای سرور رخ داد. کمی بعد دوباره تلاش کنید.';
  }
  return 'دریافت فهرست اتومیشن‌ها ناموفق بود. دوباره تلاش کنید.';
}

function accountsErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.status >= 500) {
    return 'خطای سرور رخ داد. کمی بعد دوباره تلاش کنید.';
  }
  return 'دریافت فهرست حساب‌ها ناموفق بود. دوباره تلاش کنید.';
}

export default function AutomationsPage() {
  useDocumentTitle('اتومیشن‌ها');

  const [listStatus, setListStatus] = useState<ListStatus>('loading');
  const [automations, setAutomations] = useState<ManagedAutomation[]>([]);
  const [listError, setListError] = useState<string | null>(null);
  const [listReloadKey, setListReloadKey] = useState(0);

  const [accountsStatus, setAccountsStatus] = useState<AccountsStatus>('loading');
  const [accounts, setAccounts] = useState<InstagramAccount[]>([]);
  const [accountsError, setAccountsError] = useState<string | null>(null);
  const [accountsReloadKey, setAccountsReloadKey] = useState(0);

  const [togglePendingId, setTogglePendingId] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, RowError>>({});
  const toggleInFlight = useRef(false);

  const [deleteTarget, setDeleteTarget] = useState<ManagedAutomation | null>(null);
  const [deletePending, setDeletePending] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const deleteInFlight = useRef(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listAutomationsRequest(LIST_LIMIT, 0)
      .then((response) => {
        if (!cancelled) {
          setAutomations(response.automations);
          setListError(null);
          setListStatus('ready');
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setListError(listErrorMessage(error));
          setListStatus('error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [listReloadKey]);

  useEffect(() => {
    let cancelled = false;
    listInstagramAccountsRequest()
      .then((response) => {
        if (!cancelled) {
          setAccounts(response.accounts);
          setAccountsError(null);
          setAccountsStatus('ready');
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setAccountsError(accountsErrorMessage(error));
          setAccountsStatus('error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [accountsReloadKey]);

  function handleRetryList(): void {
    setListError(null);
    setListStatus('loading');
    setListReloadKey((key) => key + 1);
  }

  function handleRetryAccounts(): void {
    setAccountsError(null);
    setAccountsStatus('loading');
    setAccountsReloadKey((key) => key + 1);
  }

  function setRowError(id: string, error: RowError | undefined): void {
    setRowErrors((current) => {
      const next = { ...current };
      if (error === undefined) {
        delete next[id];
      } else {
        next[id] = error;
      }
      return next;
    });
  }

  function handleToggle(automation: ManagedAutomation): void {
    if (toggleInFlight.current) {
      return;
    }
    toggleInFlight.current = true;
    setRowError(automation.id, undefined);
    setTogglePendingId(automation.id);
    const request = automation.enabled
      ? disableAutomationRequest(automation.id)
      : enableAutomationRequest(automation.id);
    request
      .then((response) => {
        setAutomations((current) => mergeAutomation(current, response.automation));
      })
      .catch((error: unknown) => {
        setRowError(automation.id, { kind: 'toggle', message: mutationErrorMessage(error) });
      })
      .finally(() => {
        toggleInFlight.current = false;
        setTogglePendingId(null);
      });
  }

  function handleDeleteClick(automation: ManagedAutomation): void {
    if (togglePendingId !== null) {
      return;
    }
    setDeleteError(null);
    setDeleteTarget(automation);
  }

  function handleCancelDelete(): void {
    if (deletePending) {
      return;
    }
    setDeleteTarget(null);
    setDeleteError(null);
  }

  function handleConfirmDelete(): void {
    if (deleteTarget === null || deleteInFlight.current) {
      return;
    }
    deleteInFlight.current = true;
    const target = deleteTarget;
    setDeletePending(true);
    setDeleteError(null);
    deleteAutomationRequest(target.id)
      .then(() => {
        setAutomations((current) => current.filter((row) => row.id !== target.id));
        setDeleteTarget(null);
        setDeleteError(null);
        setRowError(target.id, undefined);
      })
      .catch((error: unknown) => {
        setDeleteError(mutationErrorMessage(error));
      })
      .finally(() => {
        deleteInFlight.current = false;
        setDeletePending(false);
      });
  }

  function closeForm(): void {
    setFormOpen(false);
    setEditingId(null);
    setEditLoading(false);
    setForm(emptyForm());
    setFormErrors({});
    setFormError(null);
  }

  function handleOpenCreate(): void {
    if (accountsStatus !== 'ready' || accounts.length === 0) {
      return;
    }
    setForm(emptyForm());
    setFormErrors({});
    setFormError(null);
    setEditingId(null);
    setEditLoading(false);
    setFormOpen(true);
  }

  function handleEditClick(automation: ManagedAutomation): void {
    if (togglePendingId !== null || deletePending || editLoading) {
      return;
    }
    setRowError(automation.id, undefined);
    setForm(emptyForm());
    setFormErrors({});
    setFormError(null);
    setEditingId(automation.id);
    setEditLoading(true);
    setFormOpen(true);
    getAutomationRequest(automation.id)
      .then((response) => {
        setForm(formFromAutomation(response.automation));
        setEditLoading(false);
      })
      .catch((error: unknown) => {
        setFormOpen(false);
        setEditingId(null);
        setEditLoading(false);
        setRowError(automation.id, { kind: 'edit', message: mutationErrorMessage(error) });
      });
  }

  function handleCancelForm(): void {
    if (formSubmitting || editLoading) {
      return;
    }
    closeForm();
  }

  function handleAddKeyword(): void {
    const keyword = form.keywordDraft.trim();
    if (keyword === '') {
      setFormErrors((current) => ({ ...current, keywords: 'کلیدواژه خالی اضافه نمی‌شود.' }));
      return;
    }
    setForm((current) => ({
      ...current,
      keywords: [...current.keywords, keyword],
      keywordDraft: '',
    }));
    setFormErrors((current) => ({ ...current, keywords: undefined }));
  }

  function handleRemoveKeyword(index: number): void {
    setForm((current) => ({
      ...current,
      keywords: current.keywords.filter((_, position) => position !== index),
    }));
  }

  function handleTriggerChange(value: '' | AutomationTriggerType): void {
    const allowed = value === '' ? [] : ACTION_OPTIONS_BY_TRIGGER[value];
    setForm((current) => ({
      ...current,
      triggerType: value,
      actions: current.actions.map((action) =>
        action.action_type !== '' && !allowed.includes(action.action_type)
          ? { ...action, action_type: '' }
          : action,
      ),
    }));
    setFormErrors((current) => ({ ...current, triggerType: undefined, actions: undefined }));
  }

  function handleAddAction(): void {
    setForm((current) =>
      current.actions.length >= MAX_ACTIONS
        ? current
        : { ...current, actions: [...current.actions, { action_type: '', text: '' }] },
    );
  }

  function handleRemoveAction(index: number): void {
    setForm((current) =>
      current.actions.length <= 1
        ? current
        : { ...current, actions: current.actions.filter((_, position) => position !== index) },
    );
  }

  function handleActionChange(index: number, patch: Partial<FormAction>): void {
    setForm((current) => ({
      ...current,
      actions: current.actions.map((action, position) =>
        position === index ? { ...action, ...patch } : action,
      ),
    }));
    setFormErrors((current) => ({ ...current, actions: undefined }));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setFormError(null);
    const errors = validateForm(form);
    setFormErrors(errors);
    if (hasFormErrors(errors)) {
      return;
    }
    setFormSubmitting(true);
    const payload = buildPayload(form);
    const request =
      editingId !== null
        ? updateAutomationRequest(editingId, payload)
        : createAutomationRequest(payload);
    request
      .then((response) => {
        setAutomations((current) => mergeAutomation(current, response.automation));
        closeForm();
      })
      .catch((error: unknown) => {
        setFormError(mutationErrorMessage(error));
      })
      .finally(() => {
        setFormSubmitting(false);
      });
  }

  const accountsReady = accountsStatus === 'ready';
  const createDisabled = !accountsReady || accounts.length === 0 || formOpen;
  const rowMutationBusy = togglePendingId !== null || deletePending;
  const showSetup =
    accountsReady && accounts.length === 0 && listStatus === 'ready' && automations.length === 0;
  const actionOptions: readonly AutomationActionType[] =
    form.triggerType === '' ? [] : ACTION_OPTIONS_BY_TRIGGER[form.triggerType];

  function accountLabel(accountId: string): string {
    const account = accounts.find((item) => item.id === accountId);
    if (account !== undefined) {
      return `@${account.username}`;
    }
    return accountId;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="اتومیشن‌ها"
        description="قوانین قطعی پاسخ خودکار به کامنت‌ها، پیام‌های خصوصی و پاسخ‌های استوری"
        actions={
          <button
            type="button"
            onClick={handleOpenCreate}
            disabled={createDisabled}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Icon name="plus" className="h-4 w-4" />
            ساخت اتوماسیون
          </button>
        }
      />

      {accountsStatus === 'loading' && (
        <p role="status" className="text-sm text-slate-500">
          در حال بارگذاری حساب‌های اینستاگرام…
        </p>
      )}

      {accountsStatus === 'error' && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-rose-200 bg-white px-4 py-3">
          <p className="text-sm text-rose-700">{accountsError}</p>
          <button
            type="button"
            onClick={handleRetryAccounts}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            تلاش مجدد
          </button>
        </div>
      )}

      {listStatus === 'loading' && (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center">
          <span className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-indigo-600" />
          <p className="mt-3 text-sm text-slate-500">در حال دریافت اتومیشن‌ها…</p>
        </div>
      )}

      {listStatus === 'error' && (
        <div className="rounded-xl border border-rose-200 bg-white p-6 text-center">
          <p className="text-sm text-rose-700">{listError}</p>
          <button
            type="button"
            onClick={handleRetryList}
            className="mt-4 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            تلاش مجدد
          </button>
        </div>
      )}

      {listStatus === 'ready' && showSetup && (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50 text-indigo-400">
            <Icon name="link" className="h-6 w-6" />
          </span>
          <h3 className="mt-4 text-base font-bold text-slate-800">
            اتصال حساب اینستاگرام لازم است
          </h3>
          <p className="mx-auto mt-2 max-w-lg text-sm leading-6 text-slate-500">
            برای ساخت اتوماسیون ابتدا باید یک حساب اینستاگرام متصل کنید. تا زمانی که حسابی متصل
            نباشد، امکان ساخت اتوماسیون جدید وجود ندارد.
          </p>
          <Link
            to="/connect"
            className="mt-6 inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-indigo-800"
          >
            <Icon name="link" className="h-4 w-4" />
            اتصال اینستاگرام
          </Link>
        </div>
      )}

      {listStatus === 'ready' && !showSetup && automations.length === 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50 text-indigo-400">
            <Icon name="zap" className="h-6 w-6" />
          </span>
          <h3 className="mt-4 text-base font-bold text-slate-800">هنوز اتومیشنی وجود ندارد</h3>
          <p className="mx-auto mt-2 max-w-lg text-sm leading-6 text-slate-500">
            اولین اتوماسیون خود را برای پاسخ خودکار به کامنت‌ها و پیام‌های خصوصی بسازید.
          </p>
        </div>
      )}

      {listStatus === 'ready' && automations.length > 0 && (
        <ul data-testid="automation-list" className="space-y-4">
          {automations.map((automation) => {
            const rowError = rowErrors[automation.id];
            return (
              <li
                key={automation.id}
                data-testid="automation-row"
                data-automation-id={automation.id}
                className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5"
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-sm font-bold text-slate-900">{automation.name}</h3>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                          automation.enabled
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'bg-slate-100 text-slate-600'
                        }`}
                      >
                        {automation.enabled ? 'فعال' : 'غیرفعال'}
                      </span>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">
                        اولویت: {automation.priority}
                      </span>
                    </div>
                    <p dir="ltr" className="break-all text-left text-xs text-slate-500">
                      {accountLabel(automation.instagram_account_id)}
                    </p>
                    <div className="flex flex-wrap gap-1.5 text-xs">
                      <span className="rounded-full bg-indigo-50 px-2 py-1 text-indigo-700">
                        رویداد: {TRIGGER_LABELS[automation.trigger.type]} —{' '}
                        {MATCH_LABELS[automation.trigger.match_type]} —{' '}
                        {automation.trigger.keywords.join('، ')}
                      </span>
                      {automation.actions.map((action) => (
                        <span
                          key={`${automation.id}-${action.action_order}`}
                          className="rounded-full bg-violet-50 px-2 py-1 text-violet-700"
                        >
                          {ACTION_LABELS[action.action_type] ?? action.action_type}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleEditClick(automation)}
                      disabled={rowMutationBusy || editLoading}
                      className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {editLoading && editingId === automation.id ? 'در حال بارگذاری…' : 'ویرایش'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleToggle(automation)}
                      disabled={rowMutationBusy}
                      className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                        automation.enabled
                          ? 'border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100'
                          : 'bg-emerald-600 text-white hover:bg-emerald-800'
                      }`}
                    >
                      {togglePendingId === automation.id
                        ? 'در حال پردازش…'
                        : automation.enabled
                          ? 'غیرفعال‌سازی'
                          : 'فعال‌سازی'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteClick(automation)}
                      disabled={rowMutationBusy}
                      className="rounded-lg border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      حذف
                    </button>
                  </div>
                </div>
                {rowError !== undefined && (
                  <div role="alert" className="mt-3 flex flex-wrap items-center gap-3">
                    <p className="text-xs font-semibold text-rose-700">{rowError.message}</p>
                    <button
                      type="button"
                      onClick={() => {
                        if (rowError.kind === 'toggle') {
                          handleToggle(automation);
                        } else {
                          handleEditClick(automation);
                        }
                      }}
                      disabled={rowMutationBusy || editLoading}
                      className="rounded-lg border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      تلاش مجدد
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="automation-form-title"
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-slate-200 bg-white p-6 shadow-xl"
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 id="automation-form-title" className="text-base font-bold text-slate-900">
                {editingId !== null ? 'ویرایش اتوماسیون' : 'اتوماسیون جدید'}
              </h3>
              <button
                type="button"
                onClick={handleCancelForm}
                disabled={formSubmitting || editLoading}
                aria-label="بستن"
                className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Icon name="close" className="h-5 w-5" />
              </button>
            </div>

            {editLoading && (
              <div className="py-8 text-center">
                <span className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-indigo-600" />
                <p className="mt-3 text-sm text-slate-500">در حال بارگذاری اتوماسیون…</p>
              </div>
            )}

            {!editLoading && (
              <form onSubmit={(event) => void handleSubmit(event)} noValidate className="space-y-4">
                {formError !== null && (
                  <div
                    role="alert"
                    className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-700"
                  >
                    {formError}
                  </div>
                )}

                <div>
                  <label
                    htmlFor="automation-name"
                    className="mb-1.5 block text-sm font-medium text-slate-700"
                  >
                    نام اتوماسیون
                  </label>
                  <input
                    id="automation-name"
                    type="text"
                    dir="rtl"
                    maxLength={100}
                    value={form.name}
                    onChange={(event) => {
                      const value = event.target.value;
                      setForm((current) => ({ ...current, name: value }));
                      setFormErrors((current) => ({ ...current, name: undefined }));
                    }}
                    disabled={formSubmitting}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 disabled:bg-slate-50"
                    placeholder="مثال: خوش‌آمدگویی"
                  />
                  {formErrors.name !== undefined && (
                    <p className="mt-1 text-xs text-rose-600">{formErrors.name}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="automation-account"
                    className="mb-1.5 block text-sm font-medium text-slate-700"
                  >
                    حساب اینستاگرام
                  </label>
                  <select
                    id="automation-account"
                    value={form.accountId}
                    onChange={(event) => {
                      const value = event.target.value;
                      setForm((current) => ({ ...current, accountId: value }));
                      setFormErrors((current) => ({ ...current, accountId: undefined }));
                    }}
                    disabled={formSubmitting}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 disabled:bg-slate-50"
                  >
                    <option value="">انتخاب کنید</option>
                    {accounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        @{account.username}
                      </option>
                    ))}
                    {form.accountId !== '' &&
                      !accounts.some((account) => account.id === form.accountId) && (
                        <option value={form.accountId}>حساب انتخاب‌شده فعلی</option>
                      )}
                  </select>
                  {formErrors.accountId !== undefined && (
                    <p className="mt-1 text-xs text-rose-600">{formErrors.accountId}</p>
                  )}
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="automation-trigger"
                      className="mb-1.5 block text-sm font-medium text-slate-700"
                    >
                      رویداد مبدا
                    </label>
                    <select
                      id="automation-trigger"
                      value={form.triggerType}
                      onChange={(event) =>
                        handleTriggerChange(event.target.value as '' | AutomationTriggerType)
                      }
                      disabled={formSubmitting}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 disabled:bg-slate-50"
                    >
                      <option value="">انتخاب کنید</option>
                      {TRIGGER_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {formErrors.triggerType !== undefined && (
                      <p className="mt-1 text-xs text-rose-600">{formErrors.triggerType}</p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="automation-match"
                      className="mb-1.5 block text-sm font-medium text-slate-700"
                    >
                      عملگر تطبیق
                    </label>
                    <select
                      id="automation-match"
                      value={form.matchType}
                      onChange={(event) => {
                        const value = event.target.value;
                        setForm((current) => ({
                          ...current,
                          matchType: value as '' | AutomationMatchType,
                        }));
                        setFormErrors((current) => ({ ...current, matchType: undefined }));
                      }}
                      disabled={formSubmitting}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 disabled:bg-slate-50"
                    >
                      <option value="">انتخاب کنید</option>
                      {MATCH_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {formErrors.matchType !== undefined && (
                      <p className="mt-1 text-xs text-rose-600">{formErrors.matchType}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="automation-keyword"
                    className="mb-1.5 block text-sm font-medium text-slate-700"
                  >
                    کلمات کلیدی
                  </label>
                  <div className="flex gap-2">
                    <input
                      id="automation-keyword"
                      type="text"
                      value={form.keywordDraft}
                      onChange={(event) => {
                        const value = event.target.value;
                        setForm((current) => ({ ...current, keywordDraft: value }));
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          handleAddKeyword();
                        }
                      }}
                      disabled={formSubmitting}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 disabled:bg-slate-50"
                      placeholder="مثال: سلام"
                    />
                    <button
                      type="button"
                      onClick={handleAddKeyword}
                      disabled={formSubmitting}
                      className="shrink-0 rounded-lg border border-slate-300 px-3 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      افزودن
                    </button>
                  </div>
                  {form.keywords.length > 0 && (
                    <ul className="mt-2 flex flex-wrap gap-1.5">
                      {form.keywords.map((keyword, index) => (
                        <li
                          key={`${keyword}-${index}`}
                          className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-1 text-xs text-indigo-700"
                        >
                          {keyword}
                          <button
                            type="button"
                            onClick={() => handleRemoveKeyword(index)}
                            disabled={formSubmitting}
                            aria-label={`حذف کلیدواژه ${keyword}`}
                            className="rounded-full p-0.5 text-indigo-500 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <Icon name="close" className="h-3.5 w-3.5" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {formErrors.keywords !== undefined && (
                    <p className="mt-1 text-xs text-rose-600">{formErrors.keywords}</p>
                  )}
                </div>

                {form.triggerType === 'comment' && (
                  <div>
                    <label
                      htmlFor="automation-media-id"
                      className="mb-1.5 block text-sm font-medium text-slate-700"
                    >
                      شناسه رسانه (اختیاری)
                    </label>
                    <input
                      id="automation-media-id"
                      type="text"
                      dir="ltr"
                      inputMode="numeric"
                      value={form.mediaId}
                      onChange={(event) => {
                        const value = event.target.value;
                        setForm((current) => ({ ...current, mediaId: value }));
                        setFormErrors((current) => ({ ...current, mediaId: undefined }));
                      }}
                      disabled={formSubmitting}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-left text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 disabled:bg-slate-50"
                      placeholder="فقط برای کامنت‌های یک پست مشخص"
                    />
                    <p className="mt-1 text-xs text-slate-400">
                      برای محدود کردن کامنت‌ها به یک پست خاص، شناسه رسانه وارد کنید.
                    </p>
                    {formErrors.mediaId !== undefined && (
                      <p className="mt-1 text-xs text-rose-600">{formErrors.mediaId}</p>
                    )}
                  </div>
                )}

                <div className="space-y-3">
                  <p className="text-sm font-medium text-slate-700">اکشن‌ها</p>
                  {form.actions.map((action, index) => (
                    <div key={index} className="space-y-2 rounded-lg border border-slate-200 p-3">
                      <div className="flex items-center justify-between gap-2">
                        <label
                          htmlFor={`action-type-${index}`}
                          className="text-xs font-semibold text-slate-500"
                        >
                          اکشن {index + 1}
                        </label>
                        {form.actions.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveAction(index)}
                            disabled={formSubmitting}
                            className="text-xs font-semibold text-rose-600 hover:text-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            حذف اکشن
                          </button>
                        )}
                      </div>
                      <select
                        id={`action-type-${index}`}
                        value={action.action_type}
                        onChange={(event) =>
                          handleActionChange(index, {
                            action_type: event.target.value as '' | AutomationActionType,
                          })
                        }
                        disabled={formSubmitting}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 disabled:bg-slate-50"
                      >
                        <option value="">انتخاب کنید</option>
                        {actionOptions.map((option) => (
                          <option key={option} value={option}>
                            {ACTION_LABELS[option]}
                          </option>
                        ))}
                      </select>
                      <label htmlFor={`action-text-${index}`} className="sr-only">
                        متن اکشن {index + 1}
                      </label>
                      <textarea
                        id={`action-text-${index}`}
                        value={action.text}
                        onChange={(event) =>
                          handleActionChange(index, { text: event.target.value })
                        }
                        maxLength={2000}
                        rows={2}
                        disabled={formSubmitting}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 disabled:bg-slate-50"
                        placeholder="متن پاسخ خودکار"
                      />
                      {formErrors.actions?.[index] !== undefined && (
                        <p className="text-xs text-rose-600">{formErrors.actions[index]}</p>
                      )}
                    </div>
                  ))}
                  {form.actions.length < MAX_ACTIONS && (
                    <button
                      type="button"
                      onClick={handleAddAction}
                      disabled={formSubmitting}
                      className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      افزودن اکشن
                    </button>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="automation-priority"
                    className="mb-1.5 block text-sm font-medium text-slate-700"
                  >
                    اولویت (۰ تا ۱۰۰۰)
                  </label>
                  <input
                    id="automation-priority"
                    type="number"
                    min={0}
                    max={1000}
                    step={1}
                    value={form.priority}
                    onChange={(event) => {
                      const value = event.target.value;
                      setForm((current) => ({ ...current, priority: value }));
                      setFormErrors((current) => ({ ...current, priority: undefined }));
                    }}
                    disabled={formSubmitting}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 disabled:bg-slate-50"
                  />
                  {formErrors.priority !== undefined && (
                    <p className="mt-1 text-xs text-rose-600">{formErrors.priority}</p>
                  )}
                </div>

                <div className="flex items-center gap-2 pt-2">
                  <button
                    type="submit"
                    disabled={formSubmitting}
                    className="flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {formSubmitting && (
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                    )}
                    {formSubmitting ? 'در حال ذخیره…' : 'ذخیره'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelForm}
                    disabled={formSubmitting}
                    className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    انصراف
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {deleteTarget !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
          <div
            role="alertdialog"
            aria-modal="true"
            aria-label="تایید حذف اتوماسیون"
            className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-xl"
          >
            <h3 className="text-base font-bold text-slate-900">حذف اتوماسیون</h3>
            <p
              data-testid="delete-confirm-message"
              className="mt-2 text-sm leading-6 text-slate-600"
            >
              حذف «{deleteTarget.name}» فقط قانون تعریف‌شده را پاک می‌کند و هیچ پیامی برای
              اینستاگرام ارسال نمی‌شود. آیا مطمئن هستید؟
            </p>
            {deleteError !== null && (
              <p role="alert" className="mt-2 text-sm font-semibold text-rose-700">
                {deleteError}
              </p>
            )}
            <div className="mt-4 flex items-center gap-2">
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={deletePending}
                className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-900 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {deletePending ? 'در حال حذف…' : 'تایید حذف'}
              </button>
              <button
                type="button"
                onClick={handleCancelDelete}
                disabled={deletePending}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                انصراف
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
